import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  getPageBySlugApi,
} from "../api/PageActions";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

type ClientPage = {
  page_id: number;
  page_title: string;
  slug: string;
  route?: string;
  short_description?: string | null;
  description?: string | null;
  page_type?: string;
  status?: string;
  banner_image?: string | null;
  gallery_images?: string | null;
  gallery_videos?: string | null;
  youtube_urls?: string | null;
};

const DynamicClientPage: React.FC =
  () => {
    const { slug } =
      useParams<{
        slug: string;
      }>();

    const navigate =
      useNavigate();

    const [page, setPage] =
      useState<ClientPage | null>(
        null
      );

    const [loading, setLoading] =
      useState(true);

    const parseJsonArray = (
      value?: string | null
    ): string[] => {
      try {
        if (!value) {
          return [];
        }

        const parsed =
          JSON.parse(value);

        return Array.isArray(
          parsed
        )
          ? parsed
          : [];
      } catch {
        return [];
      }
    };

    const getMediaUrl = (
      file?: string | null
    ) => {
      if (
        !file ||
        !String(file).trim()
      ) {
        return "";
      }

      const value =
        String(file).trim();

      if (
        value.startsWith(
          "http://"
        ) ||
        value.startsWith(
          "https://"
        ) ||
        value.startsWith(
          "data:image"
        )
      ) {
        return value;
      }

      const normalized =
        value.replace(
          /\\/g,
          "/"
        );

      const uploadIndex =
        normalized.indexOf(
          "/uploads/"
        );

      if (
        uploadIndex !== -1
      ) {
        return encodeURI(
          `${API_BASE}${normalized.substring(
            uploadIndex
          )}`
        );
      }

      if (
        normalized.startsWith(
          "uploads/"
        )
      ) {
        return encodeURI(
          `${API_BASE}/${normalized}`
        );
      }

      if (
        normalized.startsWith(
          "/"
        )
      ) {
        return encodeURI(
          `${API_BASE}${normalized}`
        );
      }

      return encodeURI(
        `${API_BASE}/${normalized}`
      );
    };

    const fetchPage =
      useCallback(
        async () => {
          if (
            !slug ||
            !slug.trim()
          ) {
            setPage(null);
            setLoading(false);
            return;
          }

          try {
            setLoading(true);

            const result =
              await getPageBySlugApi(
                slug
              );

            if (
              result?.success ===
                false ||
              result?.status ===
                false
            ) {
              setPage(null);
              return;
            }

            const pageData =
              result?.page ||
              result?.data ||
              null;

            setPage(pageData);
          } catch (
            error
          ) {
            console.error(
              "Page fetch error:",
              error
            );

            setPage(null);
          } finally {
            setLoading(false);
          }
        },
        [slug]
      );

    useEffect(() => {
      fetchPage();
    }, [fetchPage]);

    if (loading) {
      return (
        <div
          style={
            styles.card
          }
        >
          <div
            className="spinner-border text-primary"
            style={{
              marginBottom:
                "15px",
            }}
          />

          <p
            style={{
              margin: 0,
              color:
                "#64748b",
            }}
          >
            Loading page...
          </p>
        </div>
      );
    }

    if (!page) {
      return (
        <div
          style={
            styles.card
          }
        >
          <h2>
            Page not found
          </h2>

          <p
            style={{
              color:
                "#64748b",
              marginBottom:
                "20px",
            }}
          >
            The requested page
            could not be found.
          </p>

          <button
            style={
              styles.button
            }
            onClick={() =>
              navigate("/")
            }
          >
            Go Home
          </button>
        </div>
      );
    }

    const galleryImages =
      parseJsonArray(
        page.gallery_images
      );

    const youtubeUrls =
      parseJsonArray(
        page.youtube_urls
      );

    return (
      <div
        style={
          styles.page
        }
      >
        <div
          style={
            styles.hero
          }
        >
          <span
            style={
              styles.badge
            }
          >
            {page.page_type ||
              "General"}
          </span>

          <h1
            style={
              styles.title
            }
          >
            {page.page_title}
          </h1>

          {page.short_description && (
            <p
              style={
                styles.subtitle
              }
            >
              {
                page.short_description
              }
            </p>
          )}
        </div>

        <div
          style={
            styles.contentCard
          }
        >
          {page.banner_image && (
            <img
              src={getMediaUrl(
                page.banner_image
              )}
              alt={
                page.page_title
              }
              style={
                styles.bannerImage
              }
              onError={(
                event
              ) => {
                event.currentTarget.style.display =
                  "none";
              }}
            />
          )}

          <div
            style={
              styles.content
            }
            dangerouslySetInnerHTML={{
              __html:
                page.description ||
                "<p>No content available.</p>",
            }}
          />

          {galleryImages.length >
            0 && (
            <div
              style={
                styles.galleryWrap
              }
            >
              <h3
                style={
                  styles.sectionTitle
                }
              >
                Gallery
              </h3>

              <div
                style={
                  styles.galleryGrid
                }
              >
                {galleryImages.map(
                  (
                    img,
                    index
                  ) => (
                    <img
                      key={
                        index
                      }
                      src={getMediaUrl(
                        img
                      )}
                      alt={`Gallery ${
                        index +
                        1
                      }`}
                      style={
                        styles.galleryImage
                      }
                      loading="lazy"
                      onError={(
                        event
                      ) => {
                        event.currentTarget.style.display =
                          "none";
                      }}
                    />
                  )
                )}
              </div>
            </div>
          )}

          {youtubeUrls.length >
            0 && (
            <div
              style={
                styles.youtubeWrap
              }
            >
              <h3
                style={
                  styles.sectionTitle
                }
              >
                Videos
              </h3>

              {youtubeUrls.map(
                (
                  url,
                  index
                ) => (
                  <a
                    key={
                      index
                    }
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    style={
                      styles.youtubeLink
                    }
                  >
                    Watch Video{" "}
                    {index +
                      1}
                  </a>
                )
              )}
            </div>
          )}

          <button
            style={
              styles.button
            }
            onClick={() =>
              navigate(-1)
            }
          >
            Back
          </button>
        </div>
      </div>
    );
  };

const styles: Record<
  string,
  React.CSSProperties
> = {
  page: {
    minHeight:
      "100vh",
    background:
      "#f8fafc",
    padding:
      "40px 16px",
  },

  hero: {
    maxWidth:
      "950px",
    margin:
      "0 auto 24px",
    background:
      "#ffffff",
    borderRadius:
      "24px",
    padding:
      "40px",
    border:
      "1px solid #e5e7eb",
    boxShadow:
      "0 8px 24px rgba(15, 23, 42, 0.06)",
  },

  badge: {
    display:
      "inline-block",
    background:
      "#eff6ff",
    color:
      "#2563eb",
    padding:
      "8px 14px",
    borderRadius:
      "999px",
    fontSize:
      "13px",
    fontWeight:
      700,
    textTransform:
      "capitalize",
    marginBottom:
      "14px",
  },

  title: {
    fontSize:
      "38px",
    fontWeight:
      800,
    margin:
      "0 0 12px",
    lineHeight:
      1.2,
    color:
      "#0f172a",
  },

  subtitle: {
    fontSize:
      "17px",
    lineHeight:
      1.7,
    maxWidth:
      "760px",
    margin: 0,
    color:
      "#475569",
  },

  contentCard: {
    maxWidth:
      "950px",
    margin:
      "0 auto",
    background:
      "#ffffff",
    borderRadius:
      "24px",
    padding:
      "34px",
    boxShadow:
      "0 8px 24px rgba(15, 23, 42, 0.05)",
    border:
      "1px solid #e5e7eb",
  },

  card: {
    maxWidth:
      "600px",
    margin:
      "80px auto",
    background:
      "#ffffff",
    borderRadius:
      "22px",
    padding:
      "32px",
    textAlign:
      "center",
    boxShadow:
      "0 8px 24px rgba(15, 23, 42, 0.05)",
    border:
      "1px solid #e5e7eb",
  },

  bannerImage: {
    width:
      "100%",
    maxHeight:
      "420px",
    objectFit:
      "cover",
    borderRadius:
      "18px",
    marginBottom:
      "26px",
  },

  content: {
    fontSize:
      "16px",
    lineHeight:
      1.9,
    color:
      "#334155",
    marginBottom:
      "28px",
    overflowWrap:
      "break-word",
    wordBreak:
      "break-word",
  },

  galleryWrap: {
    marginTop:
      "28px",
    marginBottom:
      "28px",
  },

  sectionTitle: {
    fontSize:
      "28px",
    fontWeight:
      800,
    color:
      "#0f172a",
    marginBottom:
      "18px",
  },

  galleryGrid: {
    display:
      "grid",
    gridTemplateColumns:
      "repeat(auto-fill, minmax(180px, 1fr))",
    gap:
      "16px",
  },

  galleryImage: {
    width:
      "100%",
    height:
      "200px",
    objectFit:
      "cover",
    borderRadius:
      "18px",
    border:
      "1px solid #e5e7eb",
  },

  youtubeWrap: {
    marginTop:
      "28px",
    marginBottom:
      "28px",
    display:
      "flex",
    flexDirection:
      "column",
    gap:
      "10px",
  },

  youtubeLink: {
    color:
      "#2563eb",
    fontWeight:
      700,
    textDecoration:
      "none",
  },

  button: {
    border:
      "none",
    background:
      "#2563eb",
    color:
      "#fff",
    padding:
      "12px 22px",
    borderRadius:
      "12px",
    fontWeight:
      600,
    cursor:
      "pointer",
  },
};

export default DynamicClientPage;
